import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '../services/authService';
import type { User, LoginData } from '../types/user';

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  loading: boolean;
  showTrialModal: boolean;
  login: (loginData: LoginData) => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  setUser: (user: User | null | ((prev: User | null) => User | null)) => void;
  setShowTrialModal: (show: boolean) => void;
  handleTrialModalAccept: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);

  const setUser = (userOrFunction: User | null | ((prev: User | null) => User | null)) => {
    if (typeof userOrFunction === 'function') {
      setUserState(userOrFunction);
    } else {
      setUserState(userOrFunction);
    }
  };

  const handleTrialModalAccept = () => {
    console.log('🎉 AuthContext: User accepted trial, navigating to loading page');
    setShowTrialModal(false);
    // Navigation will be handled by the Login component
  };

  useEffect(() => {
    // Listen to authentication state changes
    console.log('🔐 AuthContext: Setting up auth state listener');
    const unsubscribe = AuthService.onAuthStateChanged(async (user) => {
      console.log('🔐 AuthContext: Auth state changed, user:', user);
      if (user) {
        console.log('🔐 AuthContext: User authenticated:', {
          id: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          isActive: user.isActive
        });
        
        // Don't overwrite user state if it already has shouldShowTrialModal property
        // This prevents the onAuthStateChanged from overriding the trial modal state
        setUserState(prevUser => {
          if (prevUser && prevUser.shouldShowTrialModal !== undefined) {
            console.log('🔐 AuthContext: Preserving existing user state with trial modal flag');
            return prevUser;
          }
          console.log('🔐 AuthContext: Setting new user state from onAuthStateChanged');
          return user;
        });
        
        // OW-514 — first authenticated web session → usageStats + referral evaluate (fail-soft)
        try {
          const { recordWebSessionAndEvaluateReferral } = await import(
            '../services/referralEvaluateService'
          );
          await recordWebSessionAndEvaluateReferral(user.id, user.referenceCode);
        } catch (referralErr) {
          console.warn('🔐 AuthContext: referral evaluate skipped', referralErr);
        }

        // If this is a fresh login (no previous user state), check for trial status
        if (!user.shouldShowTrialModal) {
          console.log('🔐 AuthContext: Fresh login detected, checking trial status...');
          try {
            // Check if user should see trial modal by fetching subscription data
            const subscriptionDoc = await AuthService.getSubscriptionDoc(user.id);
            if (subscriptionDoc.exists()) {
              const subscriptionData = subscriptionDoc.data();
              const now = Date.now();
              const isTrialActive = subscriptionData.status === 'trial' &&
                subscriptionData.endDate &&
                now < parseInt(subscriptionData.endDate);
              
              if (isTrialActive) {
                console.log('🔐 AuthContext: User is in trial period, updating user state with trial modal flag');
                setUserState(prevUser => prevUser ? ({
                  ...prevUser,
                  shouldShowTrialModal: true
                }) : null);
              }
            }
          } catch (error) {
            console.error('🔐 AuthContext: Error checking trial status:', error);
          }
        }
      } else {
        console.log('🔐 AuthContext: User not authenticated');
        setUserState(user);
      }
      setLoading(false);
    });

    return () => {
      console.log('🔐 AuthContext: Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  const login = async (loginData: LoginData) => {
    try {
      setLoading(true);
      const userData = await AuthService.signIn(loginData);
      setUserState(userData);
      
      // The Login component will handle trial modal display based on userData.shouldShowTrialModal
      console.log('✅ AuthContext: User data loaded, trial modal will be handled by Login component');
    } catch (error) {
      setLoading(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      console.log('🚪 Debug - AuthContext: Starting logout process');
      
      // Clear all cached data before signing out
      // Note: We can't directly access other contexts here, so we'll rely on
      // the components to clear their own data when user becomes null
      
      await AuthService.signOut();
      setUserState(null);
      
      console.log('✅ Debug - AuthContext: Logout completed successfully');
    } catch (error) {
      console.error('❌ Debug - AuthContext: Logout error:', error);
      // Even if logout fails, clear the user state
      setUserState(null);
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      setLoading(true);
      await AuthService.sendPasswordReset(email);
    } catch (error) {
      setLoading(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoggedIn: !!user, 
      loading, 
      showTrialModal,
      login, 
      logout,
      sendPasswordReset,
      setUser,
      setShowTrialModal,
      handleTrialModalAccept
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 